import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getConversation } from 'api/tickets';
import { fetchQuickReplies } from 'api/quickReplies';
import castArray from 'lodash/castArray';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import noop from 'lodash/noop';
import partial from 'lodash/partial';
import { setChatAlertActiveId } from 'actions';
import * as analytics from 'analytics/quickReplyEventAnalytics';
import { AVAILABLE } from 'constants/expertAvailability';
import { INBOUND } from 'constants/messageDirection';
import { CUSTOMER } from 'constants/sources';
import { usePageVisibility, usePrevious } from 'customHooks';
import { getChatAlertActiveId, extractOwnAvailability } from 'extractFromState';
import { showNotification } from '../../util/util';
import { handleError } from 'services/ErrorService/ErrorService';

const NOTIFICATION_TITLE = 'Simplr Chat';
const NOTIFICATION_BODY = 'A customer is waiting! Click here to respond.';
const NOTIFICATION_SOUND = '/sounds/nominated_chat_notification.mp3';

/**
 * Returns an object with the following properties:
 * - isLoading
 * - error
 * - quickRepliesList - quick responses recommendations list that gets returned from the ML service
 * - recentMessageBodies - the most recent message(s) in the ticket's conversation
 * - trackQuickRepliesDisplayed - analytics function to be called when quick replies are displayed
 */
export const useChatQuickReplies = (ticket, isQuickRepliesEnabled) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [recentMessageBodies, setRecentMessageBodies] = useState([]);
  const [analyticsPayload, setAnalyticsPayload] = useState(null);

  const { ticketId, simplrAccountPtrId, expertId, lastMessageId } = ticket;

  // Run whenever `lastMessageId` changes.
  useEffect(() => {
    (async () => {
      if (!ticketId) {
        return;
      }

      let conversation = {};

      try {
        setIsLoading(true);
        conversation = await getConversation(ticketId);
      } catch (err) {
        setIsLoading(false);
        setError(err);

        return;
      }

      try {
        const { allMessages: messageIds } = conversation;
        const allMessages = messageIds.map((id) => conversation[id]);

        const messageBodies = getRecentMessageBodies(allMessages);
        setRecentMessageBodies(messageBodies);

        if (isQuickRepliesEnabled) {
          const { hasQuickReplies, quickRepliesRecommended, quickRepliesAnalyticsData, originalResponseData } =
            await fetchQuickReplies({ ticketId, simplrAccountPtrId }, allMessages);

          analytics.trackQuickReplyFetched(
            {
              ticketId,
              simplrAccountPtrId,
              expertId,
            },
            originalResponseData
          );

          if (hasQuickReplies) {
            setQuickReplies(quickRepliesRecommended);
            setAnalyticsPayload(quickRepliesAnalyticsData);
          } else {
            setQuickReplies([]);
          }
        }
      } catch (e) {
        handleError({ error: e, customMessage: 'Could not fetch quick replies' });
      } finally {
        setIsLoading(false);
      }
    })();

    // Returns the [lastCustomerMessage] if the customer was the last to respond; otherwise, returns [lastCustomerMessage, lastExpertMessage].
    function getRecentMessageBodies(messages) {
      if (isEmpty(messages)) {
        return [];
      }

      const lastMessage = last(messages);

      if (lastMessage.source === CUSTOMER) {
        return castArray(lastMessage.messagePlain);
      }

      const lastCustomerMessage = [...messages].reverse().find((message) => message.source === CUSTOMER);

      return [lastCustomerMessage, lastMessage].map(({ messagePlain }) => messagePlain);
    }
  }, [expertId, simplrAccountPtrId, ticketId, lastMessageId, isQuickRepliesEnabled]);

  return {
    isLoading,
    error,
    quickReplies,
    recentMessageBodies,
    trackQuickRepliesDisplayed: partial(analytics.trackQuickReplyDisplayed, ticket, analyticsPayload),
  };
};

const useChatTabIndicator = () => {
  const [isIndicatorVisible, setIsIndicatorVisible] = useState(false);

  const showIndicator = useCallback(() => {
    setIsIndicatorVisible(true);
  }, []);

  const hideIndicator = useCallback(() => {
    setIsIndicatorVisible(false);
  }, []);

  return {
    isIndicatorVisible,
    showIndicator,
    hideIndicator,
  };
};

// Display notification when customer replies.
export const useCustomerReplyNotification = (ticket, { onNotificationClick = noop } = {}) => {
  const isPageVisible = usePageVisibility();

  const { isIndicatorVisible, showIndicator, hideIndicator } = useChatTabIndicator();

  const prevLastMessageId = usePrevious(ticket.lastMessageId);

  useEffect(() => {
    if (ticket.unreadCount === 0) {
      hideIndicator();
    }
  }, [ticket.unreadCount, hideIndicator]);

  useEffect(() => {
    const isCustomerReply = ticket.lastMessageDirection === INBOUND; // `ticket.lastMessageSource` is not reliable.
    const hasNewMessage = ticket.lastMessageId !== prevLastMessageId;

    if (!(isCustomerReply && hasNewMessage)) {
      return;
    }

    showIndicator();

    if (!isPageVisible) {
      // Do not pass the `audioFile` arg. Reply sounds are handled by `ticketActions.playNewMessageAlert`.
      showNotification(NOTIFICATION_TITLE, NOTIFICATION_BODY, null, onNotificationClick, {
        requireInteraction: true,
      });
    }
  }, [
    isPageVisible,
    prevLastMessageId,
    ticket.lastMessageId,
    ticket.lastMessageDirection,
    ticket.ticketId,
    showIndicator,
    onNotificationClick,
  ]);

  return { isIndicatorVisible, showIndicator, hideIndicator };
};

// Display notification when the "update period" has elapsed, and customer hasn't replied.
export const useExpertTimeoutNotification = (ticket, { timeoutPeriod = 0, onNotificationClick = noop } = {}) => {
  const isExpertAvailable = useSelector(extractOwnAvailability) === AVAILABLE;
  const isPageVisible = usePageVisibility();

  // `timerRef` stores a timer for notifying experts when they need to update a customer.
  const timerRef = useRef(null);

  // Clear timer when component unmounts.
  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  // Clear timer when the expert goes offline.
  useEffect(() => {
    if (!isExpertAvailable) {
      clearTimeout(timerRef.current);
    }
  }, [isExpertAvailable]);

  const { isIndicatorVisible, showIndicator, hideIndicator } = useChatTabIndicator();

  // `notificationCbRef` stores the cb that gets passed to `setTimeout`. The cb impl depends on `isPageVisible`,
  // which may change before the timer is up and will execute after the effect that created it is cleaned up.
  const notificationCbRef = useRef(noop);

  const prevLastMessageId = usePrevious(ticket.lastMessageId);

  useEffect(() => {
    notificationCbRef.current = isPageVisible
      ? showIndicator
      : partial(showNotification, NOTIFICATION_TITLE, NOTIFICATION_BODY, NOTIFICATION_SOUND, onNotificationClick, {
          requireInteraction: true,
        });

    const isCustomerReply = ticket.lastMessageDirection === INBOUND;

    // Customer replies trigger their own notifications, so they should cancel any expert timeout notification timers.
    if (isCustomerReply) {
      clearTimeout(timerRef.current);
      hideIndicator();

      return;
    }

    const isSameMessage = ticket.lastMessageId === prevLastMessageId;

    if (isSameMessage) {
      return;
    }

    // A new timer will be set, so clear the old timer.
    clearTimeout(timerRef.current);
    hideIndicator();

    if (timeoutPeriod > 0) {
      timerRef.current = setTimeout(() => notificationCbRef.current(), timeoutPeriod);
    }
  }, [
    isPageVisible,
    prevLastMessageId,
    ticket.lastMessageId,
    ticket.lastMessageDirection,
    ticket.ticketId,
    timeoutPeriod,
    showIndicator,
    hideIndicator,
    onNotificationClick,
  ]);

  return { isIndicatorVisible, showIndicator, hideIndicator };
};

export const useAssignedChatPopover = (ticket) => {
  const dispatch = useDispatch();

  const chatAlertActiveId = useSelector(getChatAlertActiveId);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    if (chatAlertActiveId && chatAlertActiveId === ticket.ticketId) {
      setIsPopoverOpen(true);
    } else {
      setIsPopoverOpen(false);
    }
  }, [chatAlertActiveId, ticket.ticketId]);

  const openPopover = useCallback(() => {
    dispatch(setChatAlertActiveId(ticket.ticketId));
  }, [dispatch, ticket.ticketId]);

  const closePopover = useCallback(() => {
    dispatch(setChatAlertActiveId(null));
  }, [dispatch]);

  return {
    isPopoverOpen,
    openPopover,
    closePopover,
  };
};
